import React, { useEffect, useMemo, useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import Drawer from 'devextreme-react/drawer';
import ScrollView from 'devextreme-react/scroll-view';
import { Template } from 'devextreme-react/core/template';
import AntMainHeader from '../AntMainHeader';
import AntNavigationMenu from '../AntNavigationMenu';
import AntNavigationMenuV2 from '../AntNavigationMenuV2';
import AntRightSidebar from '../AntRightSidebar';
import AntAuth from '../../utils/AntAuth';
import AntLocalization from '../../utils/AntLocalization';
import AntMainFooter from '../AntMainFooter';
import useServiceQuotaNotice from '../AntMainFooter/useServiceQuotaNotice';
import { useNavigate } from 'react-router-dom';

import { AntGlobal, AntSession, AntSidebarContext } from '../../utils/AntContext';

import './index.scss';
import { isScreenLarge, isScreenXSmall } from '../../utils/screen';

const loadMenuConfig = () => {
    return {
        // menuMode: isScreenLarge() ? 'shrink' : 'overlap',
        menuMode: 'shrink',
        // menuRevealMode: isScreenXSmall() ? 'slide' : 'expand',
        menuRevealMode: 'expand',
        // closedMinWidth: isScreenLarge() ? 0 : 40,
        closedMinWidth: 96, // đủ rộng để hiện đầy đủ nhãn module (2-3 từ) dưới icon khi thu gọn
        // shaderEnabled:  !isScreenLarge()
        shaderEnabled: false
    };
};

const MENU_WIDTH_DEFAULT = 200;
const MENU_WIDTH_MIN = 200;
const MENU_WIDTH_MAX = 420;
const MENU_WIDTH_STORAGE_KEY = 'ant_nav_drawer_width';

const clampMenuWidth = (w) => Math.min(MENU_WIDTH_MAX, Math.max(MENU_WIDTH_MIN, Math.round(w)));

const loadMenuWidth = () => {
    try {
        const saved = parseInt(localStorage.getItem(MENU_WIDTH_STORAGE_KEY), 10);
        if (saved) return clampMenuWidth(saved);
    } catch (e) {}
    return MENU_WIDTH_DEFAULT;
};

export default function (props) {
    const navigate = useNavigate();
    const appGlobal = AntGlobal();
    const session = AntSession();

    // const [menuOpened, setMenuOpened] = React.useState(isScreenLarge());
    const [menuOpened, setMenuOpened] = React.useState(true);
    const [temporaryMenuOpened, setTemporaryMenuOpened] = React.useState(false);
    const [menuConfig, setMenuConfig] = React.useState(loadMenuConfig());

    const refScrollView = useRef();

    // ─── Resize drawer (kéo cạnh phải khi menu đang mở) ─────────
    const [menuWidth, setMenuWidth] = useState(loadMenuWidth);
    const [menuResizing, setMenuResizing] = useState(false);
    const menuWidthRef = useRef(menuWidth);
    menuWidthRef.current = menuWidth;

    const onMenuResizeStart = useCallback((e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = menuWidthRef.current;
        setMenuResizing(true);

        const onMove = (ev) => setMenuWidth(clampMenuWidth(startWidth + ev.clientX - startX));
        const onUp = () => {
            setMenuResizing(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            try { localStorage.setItem(MENU_WIDTH_STORAGE_KEY, String(menuWidthRef.current)); } catch (err) {}
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, []);

    const onMenuResizeReset = useCallback(() => {
        setMenuWidth(MENU_WIDTH_DEFAULT);
        try { localStorage.removeItem(MENU_WIDTH_STORAGE_KEY); } catch (err) {}
    }, []);

    // ─── Sidebar context (dual sidebar support) ─────────────────
    const [sidebarPageContext, setSidebarPageContext] = useState(null);
    const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
    const [pendingLeftPanel, setPendingLeftPanel] = useState(null); // { panel: 'comment', focus: true }
    const [rightSidebarOpen, setRightSidebarOpen] = useState(function () {
        try {
            var saved = localStorage.getItem('ant_right_sidebar');
            if (saved) { var s = JSON.parse(saved); return s && s.isOpen !== undefined ? s.isOpen : false; }
        } catch (e) {}
        return window.innerWidth >= 1600;
    });

    const sidebarContextValue = useMemo(function () {
        return {
            context: sidebarPageContext,
            setContext: setSidebarPageContext,
            leftSidebarOpen: leftSidebarOpen,
            setLeftSidebarOpen: setLeftSidebarOpen,
            rightSidebarOpen: rightSidebarOpen,
            setRightSidebarOpen: setRightSidebarOpen,
            menuOpened: menuOpened,
            setMenuOpened: setMenuOpened,
            // Cho phép component khác yêu cầu mở left sidebar ở tab cụ thể
            pendingLeftPanel: pendingLeftPanel,
            openLeftPanel: function (panel, opts) {
                setPendingLeftPanel({ panel: panel, focus: opts && opts.focus, _ts: Date.now() });
            },
            clearPendingLeftPanel: function () { setPendingLeftPanel(null); }
        };
    }, [sidebarPageContext, leftSidebarOpen, rightSidebarOpen, menuOpened, pendingLeftPanel]);

    const onCloseMenu = useCallback(() => {
        // if (!menuConfig || !menuConfig.shaderEnabled) return false;

        setMenuOpened(false);
        return true;
    }, [menuConfig]);

    const onNavigationChanged = useCallback(
        (event) => {
            const path = event.itemData.path;

            if (path && menuOpened) {
                navigate(path);
                if (refScrollView && refScrollView.current) refScrollView.current.instance.scrollTo(0);
            } else {
                setMenuOpened(true);
            }
        },
        [menuOpened]
    );

    const onOptionChanged = useCallback((event) => {
        console.log('ant-log-event : ', event);
    }, []);

    useEffect(() => {
        window.addEventListener('resize', () => setMenuConfig(loadMenuConfig()));
    }, []);

    const { appInfo, appAssets, appModules } = appGlobal;
    const { navigateItems, userMenuItems } = props;

    const showMenu =
        props.navigateItems.length > 1 ||
        (props.navigateItems.length === 1 && props.navigateItems[0].items instanceof Array && props.navigateItems[0].items.length > 0);

    const initFooterString = useCallback((_session) => {
        let strValue = '';

        if (_session.account_status === 'Inactive')
            strValue = `© 2026 ante.vn | ${_session.business_short_code} : ${AntLocalization.translateText({ vi: 'Tài khoản hệ thống đang tạm khóa ! Vui lòng liên hệ quản trị', en: 'Your business account is temporarily locked! Please contact the administrator', fr: 'Votre compte professionnel est temporairement verrouillé ! Veuillez contacter l\'administrateur', es: '¡Su cuenta empresarial está temporalmente bloqueada! Por favor contacte al administrador', id: 'Akun bisnis Anda dikunci sementara! Silakan hubungi administrator', ja: 'ビジネスアカウントが一時的にロックされています！管理者に連絡してください', zh: '您的企业账户已被暂时锁定！请联系管理员', ko: '비즈니스 계정이 일시적으로 잠겼습니다! 관리자에게 문의하세요', hi: 'आपका व्यापार खाता अस्थायी रूप से लॉक है! कृपया व्यवस्थापक से संपर्क करें', ar: 'حسابك التجاري مقفل مؤقتاً! يرجى الاتصال بالمسؤول' })}.`;
        else if (_session.warningInfos instanceof Array && _session.warningInfos.length > 0) {
            strValue = `© 2026 ante.vn | ${_session.business_short_code} : ${AntLocalization.translateText(
                _session.warningInfos[0].message
            )}.`;
        } else {
            strValue = `© 2026 ante.vn | ${_session.business_short_code}`;
            const modeText =
                AntAuth.getWorkspaceMode() === 'Accounting'
                    ? { vi: 'Kế toán', en: 'Accounting', fr: 'Comptabilité', es: 'Contabilidad', id: 'Akuntansi', ja: '会計', zh: '会计', ko: '회계', hi: 'लेखांकन', ar: 'المحاسبة' }
                    : { vi: 'Vận hành', en: 'Operations', fr: 'Opérations', es: 'Operaciones', id: 'Operasional', ja: '業務', zh: '业务', ko: '업무', hi: 'संचालन', ar: 'العمليات' };
            strValue = `${strValue} | ${AntLocalization.translateText(modeText)}`;
            if (_session.store_name) strValue = `${strValue} | ${_session.store_name}`;
        }

        return strValue;
    }, []);

    const strFooter = useMemo(() => initFooterString(session), [session]);
    // Vượt hạn mức gói dịch vụ: admin bấm để mở màn Hệ thống.
    const quotaNotice = useServiceQuotaNotice(session, { onOpenBilling: () => navigate('/setting/sys') });

    return (
        <AntSidebarContext.Provider value={sidebarContextValue}>
            <div className={'ant-main-layout'}>
                <AntMainHeader
                    className={'ant-main-layout-header'}
                    menuToggleEnabled
                    userMenuItems={userMenuItems}
                    menuOpened={menuOpened}
                    toggleMenu={() => setMenuOpened(!menuOpened)}
                />

                <div
                    className={'ant-main-layout-body' + (menuResizing ? ' ant-main-layout-resizing' : '')}
                    style={{ position: 'relative', '--ant-nav-width': menuWidth + 'px' }}
                >
                    {showMenu && (
                        <Drawer
                            className={'ant-main-layout-drawer'}
                            position={'before'}
                            // closeOnOutsideClick={onCloseMenu}
                            openedStateMode={menuConfig.menuMode}
                            revealMode={menuConfig.menuRevealMode}
                            minSize={menuConfig.closedMinWidth}
                            maxSize={menuWidth}
                            shading={menuConfig.shaderEnabled}
                            opened={menuOpened}
                            template={'menu'}
                            // onOptionChanged={onOptionChanged}
                        >
                            <ScrollView
                                id={'ant-main-layout-drawer-scroll'}
                                ref={refScrollView}
                                className={'ant-main-layout-drawer-scroll'}
                                showScrollbar="never"
                                useNative={false}
                            >
                                <div className={'ant-main-layout-drawer-scroll-content'}>
                                    {React.Children.map(props.children, (item) => {
                                        return item.type !== AntMainFooter && item;
                                    })}
                                </div>
                            </ScrollView>
                            <Template name={'menu'}>
                                {appModules && appModules.length > 0
                                    ? <AntNavigationMenuV2 items={navigateItems} modules={appModules} menuOpened={menuOpened} toggleMenu={() => setMenuOpened(!menuOpened)} onSelectionChanged={onNavigationChanged} />
                                    : <AntNavigationMenu items={navigateItems} menuOpened={menuOpened} onSelectionChanged={onNavigationChanged} />
                                }
                            </Template>
                        </Drawer>
                    )}

                    {showMenu && menuOpened && (
                        <div
                            className="ant-main-layout-drawer-resizer"
                            style={{ left: menuWidth - 3 }}
                            onMouseDown={onMenuResizeStart}
                            onDoubleClick={onMenuResizeReset}
                        />
                    )}

                    {!showMenu && (
                        <ScrollView ref={refScrollView} className={'ant-main-layout-drawer-scroll'} showScrollbar="never" useNative={false}>
                            <div className={'ant-main-layout-drawer-scroll-content'}>
                                {React.Children.map(props.children, (item) => {
                                    return item.type !== AntMainFooter && item;
                                })}
                            </div>
                        </ScrollView>
                    )}

                    <AntRightSidebar />
                </div>

                {session && !quotaNotice && (
                    <AntMainFooter
                        type={
                            session.account_status === 'Inactive' ||
                            (session.warningInfos instanceof Array && session.warningInfos.length > 0)
                                ? 'warning'
                                : undefined
                        }
                    >
                        {strFooter}
                    </AntMainFooter>
                )}
                {session && quotaNotice && (
                    <AntMainFooter type={quotaNotice.type} onClick={quotaNotice.onClick}>
                        {`© 2026 ante.vn | ${session.business_short_code} : ${quotaNotice.text}`}
                    </AntMainFooter>
                )}
            </div>
        </AntSidebarContext.Provider>
    );
}
